import { GridApi } from '@ag-grid-community/core';
import { computed, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { AgSetColumnsVisible } from '@shared/utils';

export const FLUX_MONTH_ANALYSIS_FIELDS = [
  'previous_month_wp_estimate',
  'previous_month_invoices_received',
  'previous_month_accrual_ltd',
  'previous_month_prepaid_ltd',
  'monthly_expense_variance',
  'monthly_expense_variance_percentage',
  'monthly_invoices_variance',
  'monthly_invoices_variance_percentage',
  'monthly_accrual_prepaid_difference',
  'monthly_accrual_prepaid_difference_percentage',
];

export const FLUX_QUARTER_ANALYSIS_FIELDS = [
  'previous_quarter_total_wp',
  'previous_quarter_total_invoiced',
  'previous_quarter_total_accrual',
  'previous_quarter_total_prepaid',
  'quarterly_expense_variance',
  'quarterly_expense_variance_percentage',
  'quarterly_invoices_variance',
  'quarterly_invoices_variance_percentage',
  'quarterly_accrual_prepaid_difference',
  'quarterly_accrual_prepaid_difference_percentage',
];

export const FLUX_ANALYSIS_FIELDS = [
  ...FLUX_MONTH_ANALYSIS_FIELDS,
  ...FLUX_QUARTER_ANALYSIS_FIELDS,
];

@Injectable({ providedIn: 'root' })
export class QuarterCloseService {
  flags = injectFlags({
    fluxAnalysisToggle: 'flux_analysis_toggle',
  });

  externalColGroupToggleSignal = signal(false);
  isCurrentQuarterSelected = signal(false);

  isFluxAnalysisAvailable = computed(() => {
    return this.isCurrentQuarterSelected() && this.flags().fluxAnalysisToggle;
  });

  attributesSectionColumns: string[] = [];

  private callToggleSubject = new Subject<boolean>();

  call$ = this.callToggleSubject.asObservable();
  openMonthFieldIndex = 0;

  callToggle(data: boolean) {
    this.callToggleSubject.next(data);
  }

  private keysToToggle(value: boolean): string[] {
    return [
      ...this.attributesSectionColumns,
      'qstart_wp_ltd',
      'qstart_invoiced_ltd',
      'qstart_accrual_ltd',
      'qstart_prepaid_ltd',
      ...this.monthKeysToToggle(value),
      'qend_net_change_in_accruals',
      'qend_unpaid_invoices_ltd',
    ];
  }

  private monthKeysToToggle(value: boolean): string[] {
    const monthKeys: Array<string> = [];

    for (let index = 1; index < 4; index++) {
      if (index !== this.openMonthFieldIndex) {
        monthKeys.push(
          `qmonth${index}_monthly_wp_estimate`,
          `qmonth${index}_monthly_invoices_received`,
          `qmonth${index}_wp_ltd`,
          `qmonth${index}_invoiced_ltd`,
          `qmonth${index}_accrual_ltd`,
          `qmonth${index}_prepaid_ltd`
        );
      }

      monthKeys.push(
        `qmonth${index}_net_change_in_accruals`,
        `qmonth${index}_unpaid_invoices_ltd`,
        `qmonth${index}_advanced_payments_ltd`,
        `qmonth${index}_net_position`
      );

      if (value) {
        monthKeys.push(
          `qmonth${index}_accrual_debit`,
          `qmonth${index}_accrual_credit`,
          `qmonth${index}_prepaid_debit`,
          `qmonth${index}_prepaid_credit`
        );
      }
    }

    return monthKeys;
  }

  toggleFluxAnalysisMode(gridAPI: GridApi, value: boolean): void {
    if (value) {
      AgSetColumnsVisible({
        gridApi: gridAPI,
        keys: this.keysToToggle(value),
        visible: false,
      });

      if (localStorage.getItem(LocalStorageKey.CLOSE_SUMMARY_OPEN_MONTH_GROUP) === 'true') {
        AgSetColumnsVisible({
          gridApi: gridAPI,
          keys: FLUX_ANALYSIS_FIELDS,
          visible: true,
        });
      } else {
        AgSetColumnsVisible({
          gridApi: gridAPI,
          keys: FLUX_QUARTER_ANALYSIS_FIELDS,
          visible: true,
        });
      }
    } else {
      AgSetColumnsVisible({
        gridApi: gridAPI,
        keys: this.keysToToggle(value).filter((key) => {
          let condition = true;

          if (localStorage.getItem(LocalStorageKey.CLOSE_SUMMARY_OPEN_MONTH_GROUP) === 'false') {
            condition = condition && !key.includes(this.openMonthFieldIndex.toString());
          }

          if (
            localStorage.getItem(LocalStorageKey.CLOSE_SUMMARY_VENDOR_CONTRACT_GROUP) === 'false'
          ) {
            condition = condition && !this.attributesSectionColumns.includes(key);
          }

          return condition;
        }),
        visible: true,
      });

      AgSetColumnsVisible({
        gridApi: gridAPI,
        keys: FLUX_ANALYSIS_FIELDS,
        visible: false,
      });
    }
  }
}
